import type { QueryClient, QueryConfig } from '../../domain/interfaces/QueryClient';

/**
 * API Slice Service
 * -----------------
 * Application layer service for creating API slices
 * Orchestrates query client and endpoint configuration
 * Abstracts all query library details (RTK Query, TanStack Query, etc.)
 * 
 * Features only need to pass simple props: url, params, transformResponse
 * All query library specifics are handled internally
 */

/**
 * Query client factory type
 * Used for dependency injection from infrastructure layer
 */
type QueryClientFactory = (config: QueryConfig) => QueryClient;

/**
 * Endpoint configuration - simple props for API endpoints
 */
export interface EndpointConfig<TData = unknown, TArgs = unknown> {
  /**
   * URL path for the endpoint (can be static string or function)
   */
  url: string | ((args: TArgs) => string);
  
  /**
   * HTTP method
   */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  
  /**
   * Query parameters (for GET) or body data (for POST/PUT)
   */
  params?: (args: TArgs) => Record<string, unknown> | undefined;

  /**
   * Request body data for POST/PUT/PATCH endpoints
   */
  data?: (args: TArgs) => unknown;
  
  /**
   * Transform response from API format to domain model
   */
  transformResponse?: (response: unknown) => TData;
  
  /**
   * Cache tags for invalidation
   */
  providesTags?: string[];
  
  /**
   * Cache duration in seconds
   */
  keepUnusedDataFor?: number;

  /**
   * Send browser credentials (cookies) for this endpoint.
   * Use sparingly; required for session-based endpoints like `/user-context`.
   */
  withCredentials?: boolean;
}

/**
 * API Slice configuration
 * Generic configuration that works with any query library
 */
export interface ApiSliceConfig {
  /**
   * Unique identifier for this API slice
   * Used for namespacing in state management (Redux reducer path, Zustand store key, etc.)
   * @deprecated Use clientId instead. reducerPath is kept for backward compatibility.
   */
  reducerPath?: string;
  
  /**
   * Unique identifier for this API slice (preferred)
   * Used for namespacing in state management
   */
  clientId?: string;
  
  /**
   * Tag types for cache invalidation
   */
  tagTypes?: string[];
  
  /**
   * Default cache duration in seconds
   */
  keepUnusedDataFor?: number;
  
  /**
   * Endpoint definitions
   */
  endpoints: Record<string, EndpointConfig<unknown, unknown>>;
}

/**
 * Generic API Slice interface
 * Returned by createApiSlice - works with any query library
 */
export interface ApiSlice {
  /**
   * Query client instance
   * Provides programmatic access to endpoints (initiate, select)
   */
  readonly queryClient: QueryClient;
  
  /**
   * Endpoints for programmatic access
   * Used by repository adapters
   */
  readonly endpoints: QueryClient['endpoints'];
  
  /**
   * Optional: React hooks for this API slice
   * Adapter-specific (RTK Query hooks, TanStack Query hooks, etc.)
   * May be undefined for adapters that don't provide hooks
   */
  readonly hooks?: Record<string, (...args: unknown[]) => unknown>;
}

/**
 * Create API Slice Service
 * Application service factory that accepts query client factory for dependency injection
 * 
 * @param queryClientFactory - Factory function to create query clients (from infrastructure layer)
 * @returns Service with createApiSlice method
 */
export function createApiSliceService(queryClientFactory: QueryClientFactory) {
  return {
    /**
     * Create API slice with simplified configuration
     * Application service that orchestrates query client and endpoint creation
     * Abstracts all RTK Query details - features just pass simple props
     * 
     * @param config - API slice configuration with endpoints
     * @returns API slice instance with hooks
     */
    createApiSlice(config: ApiSliceConfig): ApiSlice {
      // Use clientId if provided, otherwise fall back to reducerPath for backward compatibility
      const clientId = config.clientId || config.reducerPath || 'api';
      
      // Create query client using injected factory (orchestration)
      const queryClient = queryClientFactory({
        clientId,
        tagTypes: config.tagTypes,
        keepUnusedDataFor: config.keepUnusedDataFor,
      });

      // Check if adapter has registerEndpoints method (for non-RTK adapters like Fetch)
      const adapter = queryClient as unknown as Record<string, unknown>;
      
      if (typeof adapter.registerEndpoints === 'function') {
        // Generic endpoint registration for non-RTK adapters
        const result = adapter.registerEndpoints(config.endpoints);
        
        return {
          queryClient: result.client,
          endpoints: result.client.endpoints,
          hooks: result.hooks,
        } as ApiSlice;
      }
      
      // RTK Query adapter - use injectEndpoints (original working code)
      if (typeof adapter.getApi === 'function') {
        const apiInstance = adapter.getApi();

        // Inject endpoints into RTK Query API using builder pattern
        // RTK Query builder types are complex and not easily typed generically
        const api = apiInstance.injectEndpoints({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query builder types are complex
          endpoints: (builder: any) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query endpoint types are complex
            const endpoints: Record<string, any> = {};
            
            Object.entries(config.endpoints).forEach(([endpointName, endpointConfig]) => {
              // Build query function
              const queryFn = (args: unknown) => {
                const url = typeof endpointConfig.url === 'function' 
                  ? endpointConfig.url(args) 
                  : endpointConfig.url;
                
                const params = endpointConfig.params?.(args);
                const data = endpointConfig.data?.(args);
                
                return {
                  url,
                  method: endpointConfig.method || 'GET',
                  ...(params && { params }),
                  ...(data !== undefined && { data }),
                  ...(endpointConfig.withCredentials !== undefined && {
                    withCredentials: endpointConfig.withCredentials,
                  }),
                };
              };

              // Use RTK Query builder to create endpoint
              endpoints[endpointName] = builder.query({
                query: queryFn,
                ...(endpointConfig.transformResponse && {
                  transformResponse: endpointConfig.transformResponse,
                }),
                ...(endpointConfig.providesTags && {
                  providesTags: endpointConfig.providesTags,
                }),
                ...(endpointConfig.keepUnusedDataFor && {
                  keepUnusedDataFor: endpointConfig.keepUnusedDataFor,
                }),
              });
            });

            return endpoints;
          },
        });

        // RTK Query mutates the API instance, so endpoints are already updated
        // RTK Query hooks type is complex and not easily typed generically
        return {
          queryClient: queryClient,
          endpoints: queryClient.endpoints,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RTK Query hooks types are complex
          hooks: api as any,
        } as ApiSlice;
      }

      throw new Error('Query client adapter must implement either registerEndpoints() or getApi() method.');
    },
  };
}

/**
 * Type for API Slice Service
 */
export type ApiSliceService = ReturnType<typeof createApiSliceService>;
